import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { Google, Apple, Facebook } from '@mui/icons-material';
import { LoginFlow, RegistrationFlow, UiNodeInputAttributes } from '@ory/client';
import { getOrySdk } from '../../services/ory';

interface SocialLoginButtonsProps {
  flow: LoginFlow | RegistrationFlow | undefined;
  disabled?: boolean;
}

interface OidcProvider {
  id: string;
  name: string;
  icon: React.ReactNode;
  bgcolor: string;
  color: string;
}

const providers: OidcProvider[] = [
  {
    id: 'google',
    name: 'Google',
    icon: <Google />,
    bgcolor: '#fff',
    color: '#757575',
  },
  {
    id: 'apple',
    name: 'Apple',
    icon: <Apple />,
    bgcolor: '#000',
    color: '#fff',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: <Facebook />,
    bgcolor: '#1877F2',
    color: '#fff',
  },
];

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({ flow, disabled }) => {
  // Check if OIDC is available in the flow
  const hasOidcMethod = flow?.ui.nodes?.some(
    (node) =>
      node.type === 'input' &&
      (node.attributes as UiNodeInputAttributes).name === 'provider'
  );

  if (!hasOidcMethod || !flow) {
    return null;
  }

  // Get CSRF token from flow
  const getCsrfToken = (): string => {
    let csrf = '';
    flow?.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        if (attrs.name === 'csrf_token' && attrs.value) {
          csrf = String(attrs.value);
        }
      }
    });
    return csrf;
  };

  const handleSocialLogin = async (providerId: string) => {
    if (!flow) return;

    const sdk = getOrySdk();
    const csrfToken = getCsrfToken();

    try {
      // For login flows
      if ('refresh' in flow) {
        await sdk.updateLoginFlow({
          flow: flow.id,
          updateLoginFlowBody: {
            method: 'oidc',
            provider: providerId,
            csrf_token: csrfToken,
          },
        });
      } else {
        // For registration flows
        await sdk.updateRegistrationFlow({
          flow: flow.id,
          updateRegistrationFlowBody: {
            method: 'oidc',
            provider: providerId,
            csrf_token: csrfToken,
          },
        });
      }
    } catch (err: any) {
      const data = err.response?.data;

      // Kratos returns a 422 with a redirect URL for OIDC
      // Check multiple possible locations for the redirect URL
      const redirectUrl =
        data?.redirect_browser_to ||
        data?.error?.redirect_browser_to ||
        (typeof data?.error?.reason === 'string' && data.error.reason.includes('redirect the browser to:')
          ? data.error.reason.split('redirect the browser to: ')[1]?.trim()
          : null);

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      console.error('Social login error:', err);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary">
          or continue with
        </Typography>
      </Divider>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {providers.map((provider) => (
          <Button
            key={provider.id}
            variant="outlined"
            fullWidth
            startIcon={provider.icon}
            disabled={disabled}
            onClick={() => handleSocialLogin(provider.id)}
            sx={{
              bgcolor: provider.bgcolor,
              color: provider.color,
              borderColor: provider.id === 'google' ? '#ddd' : provider.bgcolor,
              '&:hover': {
                bgcolor: provider.bgcolor,
                opacity: 0.9,
                borderColor: provider.id === 'google' ? '#ccc' : provider.bgcolor,
              },
            }}
          >
            Continue with {provider.name}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default SocialLoginButtons;
